import { translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import type { Transfer } from '@interfaces/transfer';
import { CheckCircle, Error, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@ovComponents/2-component/accordion/accordion';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';

type AlertReportRule = {
  type: string;
  translatedDescription: TranslatedString;
};

export const SuspiciousActivityAccordion = ({
  transfer,
}: {
  transfer: Transfer;
}) => {
  const { t } = useTranslation(['components', 'shared']);
  const theme = useTheme();

  return (
    <Accordion
      sx={{
        boxShadow: 'none !important',
        backgroundColor: transfer?.isSuspicious
          ? theme.color.negativeVariant
          : theme.color.positiveVariant,
        border: `1px solid ${transfer?.isSuspicious ? theme.color.negativeVariantPressed : theme.color.positiveVariantPressed} !important`,
        borderRadius: `${theme.borderRadius.md} !important`,
        overflow: 'hidden',
        '& .MuiSvgIcon-root': { width: '20px' },
        '& .MuiAccordionSummary-root': {
          borderTop: 'none !important',
        },
      }}
      multiple
    >
      <AccordionSummary
        expandIcon={
          transfer?.isSuspicious && (
            <ExpandMore sx={{ color: theme.color.onSurface }} />
          )
        }
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {transfer?.isSuspicious ? (
            <Error color='error' />
          ) : (
            <CheckCircle color='success' />
          )}
          <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
            {transfer?.isSuspicious
              ? t('components:suspiciousActivityDetected')
              : t('components:noSuspiciousActivity')}
          </Typography>
        </Box>
      </AccordionSummary>
      {transfer?.alertReport && (
        <AccordionDetails sx={{ py: 0, pb: 0.5 }}>
          {transfer?.alertReport?.rules.map(
            (rule: AlertReportRule, index: number) => (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,

                  borderBottom:
                    index !== (transfer?.alertReport?.rules.length ?? 0) - 1
                      ? `1px solid ${theme.color.negativeVariantHover}`
                      : 'none',
                }}
              >
                <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
                  {t(`components:ruleTypes.${rule.type}`)}
                </Typography>
                <Typography variant='bodySmall' sx={{ textAlign: 'right' }}>
                  {translateBackend(rule.translatedDescription)}
                </Typography>
              </Box>
            ),
          )}
        </AccordionDetails>
      )}
    </Accordion>
  );
};
